import AsyncStorage from '@react-native-async-storage/async-storage';
import 'react-native-get-random-values';
import { v4 as uuidv4 } from 'uuid';

import { State, StateInvoke } from '@people-science/state-machine';

import api, { createNamedError, FetchError } from '../../../api/api';
import { StudyParticipant } from '../../../models/StudyParticipant';
import { ServicePlugin } from '../ServicePlugin';

const RETRY_DELAYS_MS = [1000, 3000];
const JSON_READ_TIMEOUT_MS = 10_000;
const SESSION_EXPIRED = {
  statusCode: 401,
  error: 'Unauthorized',
  message: 'Your session has expired. Please sign in again.'
};

export interface AssignStudyTemplateInvokeData {
  studyParticipantId: number;
  studyTemplateId: number;
}

const isAbortError = (err: any): boolean => err?.name === 'AbortError';

/**
 * The read/generate/write of the idempotency key is not atomic, so concurrent
 * double-fires could each read null and generate different keys — two distinct
 * server-side assignments. Sharing one in-flight lookup per storage key
 * serializes it (module scope covers concurrent invocations in one app
 * session; a relaunch re-reads the persisted key).
 */
const pendingKeyLookups = new Map<string, Promise<string>>();

const getOrCreateIdempotencyKey = (storageKey: string): Promise<string> => {
  const pending = pendingKeyLookups.get(storageKey);
  if (pending) return pending;
  const lookup = (async () => {
    try {
      const stored = await AsyncStorage.getItem(storageKey);
      if (stored) return stored;
      const generated = uuidv4();
      await AsyncStorage.setItem(storageKey, generated);
      return generated;
    } finally {
      pendingKeyLookups.delete(storageKey);
    }
  })();
  pendingKeyLookups.set(storageKey, lookup);
  return lookup;
};

const shouldRetry = (err: any): boolean => {
  if (!err) return false;
  if (isAbortError(err)) return false;
  if (err?.name === 'TimeoutError') return false;
  if (err?.error === 'Unauthorized') return false;
  if (err?.statusCode === 401 || err?.statusCode === 403) return false;
  if (err?.error === 'InvalidResponseBody') return true;
  if (typeof err.statusCode === 'number') return err.statusCode >= 500;
  return true;
};

const sleep = (ms: number, signal: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(createNamedError('AbortError', 'Aborted'));
      return;
    }
    const timeoutId = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timeoutId);
      reject(createNamedError('AbortError', 'Aborted'));
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });

const readJsonWithTimeout = async <T>(response: Response): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      response.json(),
      new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(createNamedError('TimeoutError', 'Response body timed out'));
        }, JSON_READ_TIMEOUT_MS);
      })
    ]);
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
};

/**
 * State-machine service for assigning a study template to a participant. Ported
 * from useAssignStudyTemplate (CD-75/76/89/136/149 retry + 502/JSON-parse
 * guards) into a signal-driven service so the form state machine owns the async
 * work, cancellation, and onDone/onError routing (CD-207). Cancellation is
 * driven by the AbortSignal the machine passes (state exit / unmount), so no
 * manual ref bookkeeping is needed in the plugin.
 */
const assignStudyTemplateService = async (
  _state: State<any> | undefined,
  invoke: StateInvoke[],
  { signal }: { signal: AbortSignal }
): Promise<StudyParticipant> => {
  const data = invoke[0]?.data as AssignStudyTemplateInvokeData | undefined;
  const studyParticipantId = data?.studyParticipantId;
  const studyTemplateId = data?.studyTemplateId;

  if (!studyParticipantId || studyParticipantId <= 0) {
    throw {
      statusCode: 400,
      error: 'BadRequest',
      message: 'Study participant is not available yet.'
    } as FetchError;
  }
  if (!studyTemplateId || studyTemplateId <= 0) {
    throw {
      statusCode: 400,
      error: 'BadRequest',
      message: 'No study template specified.'
    } as FetchError;
  }

  // The idempotency key survives an app kill: persisted before the first
  // attempt and only cleared on success, so a relaunch re-fire replays the
  // same key and the server no-ops instead of creating a second event set
  // (CD-208). Keyed per participant+template so a later, genuine
  // re-application (after a successful assign) gets a fresh key.
  const idempotencyStorageKey = `assignStudyTemplate:idempotencyKey:${studyParticipantId}:${studyTemplateId}`;
  let idempotencyKey: string | undefined;
  try {
    idempotencyKey = await getOrCreateIdempotencyKey(idempotencyStorageKey);
  } catch {
    // storage unavailability must not block the assign — a per-invocation key
    // still dedupes this invocation's retries server-side
    idempotencyKey = uuidv4();
  }

  const utcOffset = new Date().getTimezoneOffset() / 60;
  const path = `study-participants/${studyParticipantId}/study-template`;
  const body = { studyTemplateId, utcOffset, idempotencyKey };

  let lastError: any;
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    if (signal.aborted) throw createNamedError('AbortError', 'Aborted');
    if (attempt > 0) {
      await sleep(RETRY_DELAYS_MS[attempt - 1], signal);
    }
    try {
      const { response, abort } = api.put(path, body);
      const onAbort = () => abort();
      signal.addEventListener('abort', onAbort, { once: true });
      let result: Response;
      try {
        result = await response;
      } finally {
        signal.removeEventListener('abort', onAbort);
      }

      if (result.ok) {
        let json: StudyParticipant;
        try {
          json = await readJsonWithTimeout<StudyParticipant>(result);
        } catch (err) {
          if ((err as Error)?.name === 'TimeoutError') {
            lastError = err;
          } else if (
            !(result.headers.get('content-type') || '').includes(
              'application/json'
            )
          ) {
            lastError = { ...SESSION_EXPIRED, statusCode: result.status };
          } else {
            lastError = {
              statusCode: result.status,
              error: 'InvalidResponseBody',
              message: result.statusText || 'Invalid response body'
            };
          }
          if (!shouldRetry(lastError)) break;
          continue;
        }
        if (signal.aborted) throw createNamedError('AbortError', 'Aborted');
        try {
          // settle the cleanup before reporting success so the machine cannot
          // advance while the completed key is still on disk; an app kill in
          // this window (or a removal failure) leaves a stale key whose only
          // cost is one replayed server-side no-op on the next genuine assign
          await AsyncStorage.removeItem(idempotencyStorageKey);
        } catch {
          // a cleanup failure must not fail a successful assign
        }
        return json;
      }

      try {
        lastError = await readJsonWithTimeout(result);
      } catch (err) {
        if ((err as Error)?.name === 'TimeoutError') throw err;
        if (result.status === 401 || result.status === 403) {
          lastError = { ...SESSION_EXPIRED, statusCode: result.status };
        } else {
          // Non-JSON / unparseable error bodies (e.g. ALB 502 HTML pages) are
          // infra errors, not auth failures — preserve the status so 5xx stays
          // retryable and the plugin does not log the participant out
          // (CD-136/149 family).
          lastError = {
            statusCode: result.status,
            message: result.statusText || 'Request failed'
          };
        }
      }
      if (!shouldRetry(lastError)) break;
    } catch (err) {
      if (isAbortError(err)) throw err;
      lastError = err;
      if (!shouldRetry(err)) break;
    }
  }

  throw lastError;
};

export const AssignStudyTemplateServicePlugin: ServicePlugin = {
  type: 'assignStudyTemplate',
  service: assignStudyTemplateService
};
